// uploadFile.js
import fetch from '../resource/customFetch.js';
import getUploadedBytes from './getUploadedBytes.js';
import { upload as uploadDebug } from '../resource/debug.js';
import { getIsInNext } from './getJsEnvironment.js';

const MAXIMUM_RETRY_TIMES = 240; // TODO: remote config
const RETRY_COOLDOWN_SECONDS = 15; // TODO: remote config

const uploadLog = uploadDebug.extend('log:resource/uploadFile.js');
const uploadError = uploadDebug.extend('error:resource/uploadFile.js');

/**
 * Create resumable upload request
 * @param {string} url - the url of starting upload
 * @param {string} tencentUploadUrl - the url of tencent upload
 * @param {Blob} file - file blob
 * @param {Instance} xhr - xhr instance
 * @param {string} fileMd5 - file md5
 * @param {boolean} isVideo - is upload video.
 * @param {number} {[retryMaximumTimes = MAXIMUM_RETRY_TIMES]} - maximum retry times.
 * @param {number} {[retryCooldownSeconds = RETRY_COOLDOWN_SECONDS]} - retry cooldown seconds between two retry.
 * @param {function} {onError} - error handler function.
 * @param {function} {onLoad} - load handler function.
 * @param {function} {onUploadProgress} - upload progress handler function.
 * @param {function} {onRetry} - retry handler function.
 */
async function upload({
  retryMaximumTimes = MAXIMUM_RETRY_TIMES,
  retryCooldownSeconds = RETRY_COOLDOWN_SECONDS,
  tencentUploadUrl,
  url,
  file,
  xhr,
  fileMd5,
  isVideo,
  onError = () => {},
  onLoad = () => {},
  onUploadProgress = () => {},
  onRetry = () => {},
  onAbort = () => {},
  onLoadstart = () => {},
}) {
  uploadLog('execute upload');
  try {
    const useTencentUpload = !!tencentUploadUrl;
    const resumableURL = useTencentUpload
      ? tencentUploadUrl
      : await getUploadUrl({
          uploadUrl: url,
          file,
          fileMd5,
          isVideo,
        });
    let retried = 0;
    xhr.open('PUT', resumableURL, true);

    xhr.setRequestHeader('Content-Type', file.type);
    if (tencentUploadUrl) {
      xhr.setRequestHeader(
        'Authorization',
        new URL(tencentUploadUrl).searchParams.toString()
      );
      xhr.setRequestHeader('Content-MD5', fileMd5);
    }

    xhr.addEventListener('loadstart', onLoadstart);
    xhr.onload = onLoad;
    xhr.upload.onprogress = event => {
      retried = 0;
      onUploadProgress(event);
    };
    xhr.onabort = onAbort;
    xhr.onerror = event => {
      // Retry upload if use google upload.
      // Won't trigger xhr.onabort when xhr has error during execute xhr.abort(). Needs to check xhr.readyState and xhr.status to know xhr is aborted.
      uploadError('xhr onError', { event });
      const isAborted = xhr.readyState === 0 && xhr.status === 0;
      if (!useTencentUpload && !isAborted && retried < retryMaximumTimes) {
        retried += 1;
        setTimeout(() => {
          onRetry();
          resumableUploadFile({ resumableURL, xhr, file });
        }, retryCooldownSeconds * 1000);
      } else {
        onError(event);
      }
    };
    xhr.send(file);
  } catch (error) {
    uploadError('error at upload', { error });
    onError(error);
  }
}

/**
 * Get the real upload url
 * @param {string} {uploadUrl} - the url of starting upload
 * @param {Blob} {file} - file blob
 * @param {string} {fileMd5} - file md5
 * @param {boolean} {isVideo} - is upload video.
 * @return {Promise<string>} the url string for uploading file
 */
async function getUploadUrl({ uploadUrl, file, fileMd5, isVideo }) {
  const url = new URL(uploadUrl);
  const fetchOptions = {
    method: 'POST',
    headers: {
      'x-goog-resumable': 'start',
      'Content-Type': isVideo ? 'video/mp4' : file.type,
      'Content-MD5': fileMd5,
      'X-Goog-Content-Length-Range': `${file.size},${file.size}`,
    },
    shouldUseCustomFetch: false,
  };

  const response = await fetch(url.href, fetchOptions);

  if (!response.ok) {
    throw new Error('fail_to_get_upload_url');
  }

  return response.headers.get('location');
}

/**
 * Send the resumable upload request
 * @param {string} resumableURL - the url of starting upload
 * @param {Blob} file - file blob
 * @param {Instance} xhr - xhr instance
 */
export const resumableUploadFile = async ({ resumableURL, xhr, file }) => {
  const uploadedBytes = await getUploadedBytes({ resumableURL, file });

  if (uploadedBytes == null) {
    return xhr.onerror();
  }

  const startAt = uploadedBytes + 1;

  xhr.open('PUT', resumableURL, true);
  xhr.setRequestHeader(
    'Content-Range',
    `bytes ${startAt}-${file.size - 1}/${file.size}`
  );

  return xhr.send(file.slice(startAt));
};

const resolveWorker = () =>
  getIsInNext()
    ? window.getNextScopedWorkers?.md5()
    : new Worker(window.md5WorkerFileName || '/static/js/md5Worker.js');

/**
 * @param {Blob} file - file blob
 * @returns {Promise}
 */
export const getFileMd5 = file =>
  new Promise((resolve, reject) => {
    uploadLog('execute getFileMd5');
    const isServer = typeof window === 'undefined';
    if (isServer || !window.Worker) reject();
    const md5Worker = resolveWorker();
    md5Worker.onmessage = e => {
      resolve(e.data);
      md5Worker.terminate();
    };

    md5Worker.onerror = e => {
      reject(e);
      md5Worker.terminate();
    };

    md5Worker.onmessageerror = () => {
      reject();
      md5Worker.terminate();
    };

    md5Worker.postMessage({ file });
  });
export default upload;
